import { useEffect, useRef } from 'react';

import { useLocation, useNavigate } from 'react-router-dom';

import { PRESERVED_PARAMS } from '../constants/preservedParams';

/**
 * Component that intercepts navigation and preserves special query params
 * like 'upcoming-features' across all navigation.
 *
 * This works by:
 * 1. Saving preserved params to sessionStorage when present in URL
 * 2. On navigation, checking if they're missing from new URL
 * 3. Restoring them if needed
 */
export const NavigationInterceptor: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const isRestoringRef = useRef(false);

  useEffect(() => {
    const currentParams = new URLSearchParams(location.search);

    // ALWAYS update sessionStorage with current values if present
    // This must happen even when we're in the restoration cycle
    PRESERVED_PARAMS.forEach((param) => {
      const value = currentParams.get(param);
      if (value !== null) {
        sessionStorage.setItem(`preserved_${param}`, value);
      }
    });

    // Avoid infinite loop: if we just restored params, skip restoration logic
    if (isRestoringRef.current) {
      isRestoringRef.current = false;
      return;
    }

    // Check if we have any preserved params in sessionStorage
    const savedParams: Record<string, string> = {};
    PRESERVED_PARAMS.forEach((param) => {
      const saved = sessionStorage.getItem(`preserved_${param}`);
      if (saved) {
        savedParams[param] = saved;
      }
    });

    // Check if we need to restore any preserved params
    const missingParams: [string, string][] = [];
    Object.entries(savedParams).forEach(([param, value]) => {
      if (!currentParams.has(param) && value !== null) {
        missingParams.push([param, value]);
      }
    });

    if (missingParams.length > 0) {
      // Add missing preserved params
      missingParams.forEach(([param, value]) => {
        currentParams.set(param, value);
      });

      const newSearch = currentParams.toString();
      isRestoringRef.current = true;
      navigate(`${location.pathname}${newSearch ? `?${newSearch}` : ''}`, { replace: true });
    }
  }, [location, navigate]);

  return null;
};
